import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, formatDateBR, formatDateOnlyBR, parseDate } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Loader2, RefreshCw, TrendingUp, Package, Truck, AlertTriangle, ArrowLeft, Calendar, Clock, DollarSign, Download, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { colors } from '../theme';
import { logoPrimazaBase64 } from '../assets/logo-base64';

// Função para sanitizar valores monetários
// Trata casos como "R$ 100,50", "100.50", "100,50", strings vazias, etc.
const parseValorMonetario = (valor) => {
  if (!valor || valor === '-' || valor === 'N/A' || valor === '') return 0;
  
  // Se já é número
  if (typeof valor === 'number') {
    // Valores individuais de frete raramente passam de 10.000
    if (valor > 50000 || valor < 0) return 0;
    return valor;
  }
  
  // Converte para string
  let str = String(valor).trim();
  
  // Remove "R$" e espaços
  str = str.replace(/R\$\s*/gi, '').trim();
  
  // Se está vazio após limpeza
  if (!str) return 0;
  
  // Detectar formato: se tem vírgula E ponto, é formato BR (1.234,56)
  // Se tem só vírgula, pode ser decimal BR (43,65)
  // Se tem só ponto, é formato US (43.65)
  if (str.includes(',') && str.includes('.')) {
    // Formato BR com milhar: 1.234,56 -> 1234.56
    str = str.replace(/\./g, '').replace(',', '.');
  } else if (str.includes(',') && !str.includes('.')) {
    // Só vírgula: 43,65 -> 43.65
    str = str.replace(',', '.');
  }
  // Se só tem ponto, já está no formato correto
  
  const num = parseFloat(str);
  
  // Validação: valores individuais de frete devem ser razoáveis
  if (isNaN(num) || num < 0 || num > 50000) return 0;
  
  return num;
};

// Função para formatar valores em Real brasileiro (R$ 1.234,56)
const formatarMoedaBR = (valor) => {
  if (!valor && valor !== 0) return 'R$ 0,00';
  return valor.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
};

const Analytics = ({ user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTransportadora, setLoadingTransportadora] = useState(false); // Loading ao clicar em transportadora
  const [loadingProgress, setLoadingProgress] = useState(0); // Progresso do loading
  const [selectedTransportadora, setSelectedTransportadora] = useState(null);
  const [pedidosTransportadora, setPedidosTransportadora] = useState([]);
  
  // Estados para filtro e paginação de transportadoras
  const [filtroTransportadora, setFiltroTransportadora] = useState('');
  const [paginaTransportadora, setPaginaTransportadora] = useState(1);
  const itensPorPaginaTransp = 9; // 3 colunas x 3 linhas
  
  // Estados para filtro e paginação dentro da visualização de pedidos de uma transportadora
  const [filtroPedidosTransp, setFiltroPedidosTransp] = useState('');
  const [paginaPedidosTransp, setPaginaPedidosTransp] = useState(1);
  const itensPorPaginaPedidos = 20;
  
  // Estados para filtro de período (mês/ano)
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState(''); // Padrão: todos os anos
  const [filtroTipoFrete, setFiltroTipoFrete] = useState('TODOS'); // CIF, FOB ou TODOS

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Erro ao carregar analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPedidosTransportadora = async (transportadoraNome) => {
    setLoadingTransportadora(true);
    setLoadingProgress(0);
    
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);
    
    try {
      const token = localStorage.getItem('primaza_token');
      console.log('🔍 Buscando pedidos da transportadora:', transportadoraNome);
      
      // Usar POST para evitar problemas de encoding na URL
      // Caracteres como Ã, Ç, etc podem causar problemas no GET
      const response = await axios.post(`${API}/pedidos/filtrar`, 
        {
          transportadora: transportadoraNome || ''
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000
        }
      );
      
      setLoadingProgress(100);
      
      // O endpoint retorna array de pedidos diretamente
      const pedidosData = Array.isArray(response.data) ? response.data : (response.data.pedidos || []);
      console.log('📦 Pedidos recebidos:', pedidosData.length);
      
      // Filtrar para garantir match exato (o endpoint usa regex)
      const pedidosFiltrados = pedidosData.filter(p => 
        (p.transportadora || '').toLowerCase() === (transportadoraNome || '').toLowerCase()
      );
      
      // Calcular métricas
      const pedidosComDatas = pedidosFiltrados.filter(p => p.prev_entrega && p.data_entrega_real);
      const valorTotalCalc = pedidosFiltrados.reduce((sum, p) => sum + (parseValorMonetario(p.valor_frete) || 0), 0);
      
      setPedidosTransportadora(pedidosFiltrados);
      setSelectedTransportadora({
        nome: transportadoraNome || 'Sem Transportadora',
        pedidos: pedidosFiltrados,
        total: pedidosFiltrados.length,
        entregues: pedidosFiltrados.filter(p => p.status_pedido === 'ENTREGUE').length,
        valorTotal: valorTotalCalc,
        pedidosComDatas: pedidosComDatas.length
      });
    } catch (error) {
      console.error('Erro ao carregar pedidos da transportadora:', error);
      // Mostrar alerta ao usuário
      alert(`Erro ao carregar dados da transportadora "${transportadoraNome}". Tente novamente.`);
    } finally {
      clearInterval(progressInterval);
      setLoadingProgress(0);
      setLoadingTransportadora(false);
    }
  };

  // Função para filtrar pedidos por período
  // Usa created_at (data de entrada/faturamento) para contabilizar gastos mensais
  const filtrarPedidosPorPeriodo = (pedidos) => {
    if (!filtroMes && !filtroAno && filtroTipoFrete === 'TODOS') return pedidos;
    
    return pedidos.filter(pedido => {
      // Filtro de período - usa DATA DE ENTRADA NO SISTEMA (created_at)
      if (filtroMes || filtroAno) {
        // Usar created_at (data de faturamento/entrada)
        const dataEntrada = pedido.created_at || pedido.data_nf;
        if (!dataEntrada) return false;
        
        try {
          const dataPedido = dataEntrada.split('T')[0];
          const [ano, mes] = dataPedido.split('-');
          
          if (filtroAno && ano !== filtroAno) return false;
          if (filtroMes && mes !== filtroMes) return false;
        } catch (e) {
          return false;
        }
      }
      
      // Filtro de tipo de frete
      if (filtroTipoFrete !== 'TODOS') {
        if (filtroTipoFrete === 'CIF' && pedido.tipo_frete !== 'CIF') return false;
        if (filtroTipoFrete === 'FOB' && pedido.tipo_frete !== 'FOB') return false;
      }
      
      return true;
    });
  };

  const exportarPDF = () => {
    // Aplicar filtro de período nos pedidos
    const pedidosFiltradosPeriodo = filtrarPedidosPorPeriodo(pedidosTransportadora);
    
    // Configuração A4
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });
    
    // Adicionar Logo
    try {
      doc.addImage(logoPrimazaBase64, 'PNG', 15, 12, 45, 16);
    } catch (e) {
      console.log('Erro ao adicionar logo:', e);
    }
    
    // Header melhorado
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(46, 125, 50); // Verde Primaza
    doc.text('Relatório de Performance', 70, 20);
    
    doc.setFontSize(11);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(`Transportadora: ${selectedTransportadora.nome}`, 15, 35);
    
    // Período do relatório
    let textoPeriodo = 'Período: Todos';
    if (filtroMes && filtroAno) {
      const meses = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
      textoPeriodo = `Período: ${meses[parseInt(filtroMes)-1]} de ${filtroAno}`;
    } else if (filtroAno) {
      textoPeriodo = `Período: Ano ${filtroAno}`;
    }
    
    // Adicionar tipo de frete
    if (filtroTipoFrete !== 'TODOS') {
      textoPeriodo += ` • Tipo: ${filtroTipoFrete}`;
    }
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(textoPeriodo, 15, 42);
    doc.text(`Gerado em: ${new Date().toLocaleDateString('pt-BR')} às ${new Date().toLocaleTimeString('pt-BR')}`, 15, 47);
    
    // Linha separadora verde
    doc.setDrawColor(124, 179, 66); // Verde Primaza
    doc.setLineWidth(0.5);
    doc.line(15, 51, 195, 51);
    
    // Recalcular métricas com pedidos filtrados
    const totalFiltrado = pedidosFiltradosPeriodo.length;
    const entreguesFiltrado = pedidosFiltradosPeriodo.filter(p => p.status_pedido === 'ENTREGUE').length;
    const valorTotalFiltrado = pedidosFiltradosPeriodo.reduce((sum, p) => sum + (parseValorMonetario(p.valor_frete) || 0), 0);
    const comDadosFiltrado = pedidosFiltradosPeriodo.filter(p => p.prev_entrega && p.data_entrega_real).length;
    
    // Métricas resumidas
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(46, 125, 50);
    doc.text('RESUMO EXECUTIVO', 15, 58);
    
    const metricas = [
      `Total de Pedidos: ${totalFiltrado}`,
      `Pedidos Entregues: ${entreguesFiltrado} (${totalFiltrado > 0 ? ((entreguesFiltrado / totalFiltrado) * 100).toFixed(1) : 0}%)`,
      `Valor Total Frete: R$ ${valorTotalFiltrado.toFixed(2)}`,
      `Pedidos com Dados de Prazo: ${comDadosFiltrado}`
    ];
    
    // Calcular % médio de frete (excluindo FOB e frete embutido)
    const pedidosValidosParaFrete = pedidosFiltradosPeriodo.filter(p => {
      // Excluir FOB e frete embutido
      if (p.tipo_frete === 'FOB') return false;
      if (p.cte_info && p.cte_info.frete_embutido_nf === true) return false;
      return p.valor_frete && p.valor_nf;
    });
    
    if (pedidosValidosParaFrete.length > 0) {
      const somaPercent = pedidosValidosParaFrete.reduce((sum, p) => {
        const valorFrete = parseValorMonetario(p.valor_frete);
        const valorNf = parseValorMonetario(p.valor_nf);
        // Evitar divisão por zero e valores absurdos
        if (valorNf <= 0 || valorFrete <= 0) return sum;
        const percent = (valorFrete / valorNf) * 100;
        // Se o percentual for maior que 50%, ignorar (provavelmente dado errado)
        if (percent > 50) return sum;
        return sum + percent;
      }, 0);
      const media = somaPercent / pedidosValidosParaFrete.length;
      metricas.push(`% Frete Médio: ${media.toFixed(2)}% (meta: máximo 5%)`);
      metricas.push(`Pedidos no cálculo: ${pedidosValidosParaFrete.length} (excluindo FOB/frete embutido)`);
    }
    
    let yPos = 64;
    doc.setFont(undefined, 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60, 60, 60);
    metricas.forEach(metrica => {
      doc.text(metrica, 15, yPos);
      yPos += 5;
    });
    
    // Tabela de pedidos (usando pedidos filtrados por período)
    const tableData = pedidosFiltradosPeriodo.map(pedido => {
      const prazoPromise = pedido.prev_entrega ? parseDate(pedido.prev_entrega) : null;
      const entregaReal = pedido.data_entrega_real ? parseDate(pedido.data_entrega_real) : null;
      
      let performance = '-';
      if (prazoPromise && entregaReal && !isNaN(prazoPromise.getTime()) && !isNaN(entregaReal.getTime())) {
        const diffDias = Math.ceil((entregaReal - prazoPromise) / (1000 * 60 * 60 * 24));
        if (diffDias <= 0) {
          performance = diffDias === 0 ? 'No prazo' : `${Math.abs(diffDias)}d antes`;
        } else {
          performance = `${diffDias}d atraso`;
        }
      }
      
      let percentFrete = '-';
      // Não calcular margem para FOB ou frete embutido
      const isFOB = pedido.tipo_frete === 'FOB';
      const isFreteEmbutido = pedido.cte_info && pedido.cte_info.frete_embutido_nf === true;
      
      if (isFOB || isFreteEmbutido) {
        percentFrete = 'N/A';
      } else if (pedido.valor_frete && pedido.valor_nf) {
        const percent = (parseValorMonetario(pedido.valor_frete) / parseValorMonetario(pedido.valor_nf)) * 100;
        percentFrete = `${percent.toFixed(2)}%`;
      }
      
      return [
        pedido.pedido,
        (pedido.nome_cliente || pedido.cliente || '-').substring(0, 20),
        pedido.status_pedido || '-',
        pedido.valor_nf ? `R$ ${parseValorMonetario(pedido.valor_nf).toFixed(2)}` : '-',
        pedido.valor_frete ? `R$ ${parseValorMonetario(pedido.valor_frete).toFixed(2)}` : '-',
        percentFrete,
        pedido.numero_cte || '-',
        pedido.tipo_frete || '-',
        prazoPromise ? prazoPromise.toLocaleDateString('pt-BR') : '-',
        entregaReal ? entregaReal.toLocaleDateString('pt-BR') : '-',
        performance
      ];
    });
    
    autoTable(doc, {
      startY: yPos + 3,
      head: [['Pedido', 'Cliente', 'Status', 'Valor NF', 'Frete', '% Frete', 'CTE', 'Tipo', 'Prazo', 'Entrega', 'Perf.']],
      body: tableData,
      theme: 'grid',
      margin: { left: 15, right: 15 },
      headStyles: {
        fillColor: [124, 179, 66], // Verde Primaza
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 8,
        halign: 'center'
      },
      bodyStyles: {
        fontSize: 7,
        textColor: 50,
        cellPadding: 2,
        overflow: 'linebreak',
        cellWidth: 'wrap',
        minCellHeight: 6
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      styles: {
        overflow: 'linebreak',
        cellWidth: 'wrap',
        fontSize: 7,
        cellPadding: 1.5,
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 12 }, // Pedido (reduzido)
        1: { cellWidth: 50 }, // Cliente (MUITO aumentado!)
        2: { cellWidth: 18 }, // Status
        3: { cellWidth: 18, halign: 'right' }, // Valor NF (reduzido)
        4: { cellWidth: 16, halign: 'right' }, // Frete (reduzido)
        5: { cellWidth: 12, halign: 'center' }, // % Frete (reduzido)
        6: { cellWidth: 10, halign: 'center' }, // CTE (reduzido)
        7: { cellWidth: 10, halign: 'center' }, // Tipo (reduzido)
        8: { cellWidth: 16 }, // Prazo (reduzido)
        9: { cellWidth: 16 }, // Entrega (reduzido)
        10: { cellWidth: 12, halign: 'center' } // Performance (reduzido)
      },
      didDrawPage: (data) => {
        // Footer em cada página
        const pageCount = doc.internal.getNumberOfPages();
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          `Página ${pageNumber} de ${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
        doc.setTextColor(124, 179, 66);
        doc.text('PRIMAZA - Rastreamento Animal', 15, doc.internal.pageSize.height - 10);
      }
    });
    
    // Nome do arquivo com período e tipo
    let nomeArquivo = `relatorio_${selectedTransportadora.nome.replace(/\s+/g, '_')}`;
    if (filtroMes && filtroAno) {
      nomeArquivo += `_${filtroAno}_${filtroMes}`;
    } else if (filtroAno) {
      nomeArquivo += `_${filtroAno}`;
    }
    if (filtroTipoFrete !== 'TODOS') {
      nomeArquivo += `_${filtroTipoFrete}`;
    }
    nomeArquivo += `_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`;
    
    doc.save(nomeArquivo);
  };

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: colors.primaza.primary }} />
      </div>
    );
  }

  // Se uma transportadora foi selecionada, mostrar tela de detalhes
  if (selectedTransportadora) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedTransportadora(null)}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
                  🚚 {selectedTransportadora.nome}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Análise detalhada de performance
                </p>
              </div>
            </div>
            <Button
              onClick={exportarPDF}
              className="bg-green-600 hover:bg-green-700 text-white"
              size="sm"
            >
              <Download className="h-4 w-4 mr-2" />
              Exportar PDF
            </Button>
          </div>
        </div>

        <div className="max-w-full mx-auto p-6 space-y-6">
          {/* Filtros de Período */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-gray-600" />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Filtrar por Período:</span>
                    <span className="text-xs text-gray-500 ml-2">(data de faturamento)</span>
                  </div>
                </div>
                
                <select
                  value={filtroMes}
                  onChange={(e) => setFiltroMes(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos os Meses</option>
                  <option value="01">Janeiro</option>
                  <option value="02">Fevereiro</option>
                  <option value="03">Março</option>
                  <option value="04">Abril</option>
                  <option value="05">Maio</option>
                  <option value="06">Junho</option>
                  <option value="07">Julho</option>
                  <option value="08">Agosto</option>
                  <option value="09">Setembro</option>
                  <option value="10">Outubro</option>
                  <option value="11">Novembro</option>
                  <option value="12">Dezembro</option>
                </select>
                
                <select
                  value={filtroAno}
                  onChange={(e) => setFiltroAno(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                >
                  <option value="">Todos os Anos</option>
                  <option value="2026">2026</option>
                  <option value="2025">2025</option>
                  <option value="2024">2024</option>
                  <option value="2023">2023</option>
                </select>
                
                <select
                  value={filtroTipoFrete}
                  onChange={(e) => setFiltroTipoFrete(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-semibold"
                >
                  <option value="TODOS">Todos os Tipos</option>
                  <option value="CIF">💰 CIF (Primaza paga)</option>
                  <option value="FOB">👤 FOB (Cliente paga)</option>
                </select>
                
                {(filtroMes || filtroAno || filtroTipoFrete !== 'TODOS') && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFiltroMes('');
                      setFiltroAno('');
                      setFiltroTipoFrete('TODOS');
                    }}
                  >
                    Limpar Filtros
                  </Button>
                )}
                
                <span className="text-xs text-gray-500 ml-auto">
                  {filtroMes && filtroAno ? `${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(filtroMes)-1]}/${filtroAno}` : 
                   filtroAno ? `Ano ${filtroAno}` : 
                   filtroMes ? `Mês ${['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][parseInt(filtroMes)-1]}` :
                   'Mostrando todos os períodos'}
                  {filtroTipoFrete !== 'TODOS' && ` • ${filtroTipoFrete}`}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Cards de Métricas */}
          {(() => {
            // Recalcular métricas com filtro de período aplicado
            const pedidosFiltradosPeriodo = filtrarPedidosPorPeriodo(pedidosTransportadora);
            const totalFiltrado = pedidosFiltradosPeriodo.length;
            const entreguesFiltrado = pedidosFiltradosPeriodo.filter(p => p.status_pedido === 'ENTREGUE').length;
            const valorTotalFiltrado = pedidosFiltradosPeriodo.reduce((sum, p) => sum + (parseValorMonetario(p.valor_frete) || 0), 0);
            const comDadosFiltrado = pedidosFiltradosPeriodo.filter(p => p.prev_entrega && p.data_entrega_real).length;
            
            // Calcular % médio
            const pedidosValidosParaFrete = pedidosFiltradosPeriodo.filter(p => {
              if (p.tipo_frete === 'FOB') return false;
              if (p.cte_info && p.cte_info.frete_embutido_nf === true) return false;
              return p.valor_frete && p.valor_nf;
            });
            
            let percentMedio = '-';
            if (pedidosValidosParaFrete.length > 0) {
              const somaPercent = pedidosValidosParaFrete.reduce((sum, p) => {
                const valorFrete = parseValorMonetario(p.valor_frete);
                const valorNf = parseValorMonetario(p.valor_nf);
                if (valorNf <= 0 || valorFrete <= 0) return sum;
                const percent = (valorFrete / valorNf) * 100;
                if (percent > 50) return sum;
                return sum + percent;
              }, 0);
              percentMedio = (somaPercent / pedidosValidosParaFrete.length).toFixed(2) + '%';
            }
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Package className="text-blue-600" />
                      <p className="text-sm text-gray-600">Total de Pedidos</p>
                    </div>
                    <p className="text-3xl font-bold text-blue-600">{totalFiltrado}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">✅</div>
                      <p className="text-sm text-gray-600">Entregues</p>
                    </div>
                    <p className="text-3xl font-bold text-green-600">{entreguesFiltrado}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {totalFiltrado > 0 
                        ? ((entreguesFiltrado / totalFiltrado) * 100).toFixed(1) 
                        : 0}% de entrega
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <DollarSign className="text-green-600" />
                      <p className="text-sm text-gray-600">Valor Total Frete</p>
                    </div>
                    <p className="text-3xl font-bold text-green-600">
                      {formatarMoedaBR(valorTotalFiltrado)}
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <Clock className="text-orange-600" />
                      <p className="text-sm text-gray-600">Com Dados de Prazo</p>
                    </div>
                    <p className="text-3xl font-bold text-orange-600">{comDadosFiltrado}</p>
                    <p className="text-xs text-gray-500 mt-1">pedidos com previsão e entrega</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">📊</div>
                      <p className="text-sm text-gray-600">% Frete Médio</p>
                    </div>
                    <p className="text-3xl font-bold text-purple-600">{percentMedio}</p>
                    <p className="text-xs text-gray-500 mt-1">meta: máximo 5%</p>
                  </CardContent>
                </Card>
              </div>
            );
          })()}

          {/* Cards de Métricas antigas removidas - substituídas acima */}
          <div className="hidden">
          {/* Cards de Métricas */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Package className="text-blue-600" />
                  <p className="text-sm text-gray-600">Total de Pedidos</p>
                </div>
                <p className="text-3xl font-bold text-blue-600">{selectedTransportadora.total}</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="text-2xl">✅</div>
                  <p className="text-sm text-gray-600">Entregues</p>
                </div>
                <p className="text-3xl font-bold text-green-600">{selectedTransportadora.entregues}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {selectedTransportadora.total > 0 
                    ? ((selectedTransportadora.entregues / selectedTransportadora.total) * 100).toFixed(1) 
                    : 0}% de entrega
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <DollarSign className="text-green-600" />
                  <p className="text-sm text-gray-600">Valor Total Frete</p>
                </div>
                <p className="text-3xl font-bold text-green-600">
                  {formatarMoedaBR(selectedTransportadora.valorTotal)}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <Clock className="text-orange-600" />
                  <p className="text-sm text-gray-600">Com Dados de Prazo</p>
                </div>
                <p className="text-3xl font-bold text-orange-600">{selectedTransportadora.pedidosComDatas}</p>
                <p className="text-xs text-gray-500 mt-1">pedidos com previsão e entrega</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3 mb-2">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm text-gray-600">% Frete Médio</p>
                </div>
                <p className="text-3xl font-bold text-purple-600">
                  {(() => {
                    // Filtrar pedidos válidos para cálculo, excluindo FOB e frete embutido
                    const pedidosValidos = pedidosTransportadora.filter(p => {
                      // Excluir se for FOB ou se frete estiver embutido na NF
                      if (p.tipo_frete === 'FOB') return false;
                      if (p.cte_info && p.cte_info.frete_embutido_nf === true) return false;
                      // Incluir apenas se tiver ambos os valores
                      return p.valor_frete && p.valor_nf;
                    });
                    
                    if (pedidosValidos.length === 0) return '-';
                    
                    const somaPercent = pedidosValidos.reduce((sum, p) => {
                      const valorFrete = parseValorMonetario(p.valor_frete);
                      const valorNf = parseValorMonetario(p.valor_nf);
                      if (valorNf <= 0 || valorFrete <= 0) return sum;
                      const percent = (valorFrete / valorNf) * 100;
                      if (percent > 50) return sum;
                      return sum + percent;
                    }, 0);
                    const media = somaPercent / pedidosValidos.length;
                    return `${media.toFixed(2)}%`;
                  })()}
                </p>
                <p className="text-xs text-gray-500 mt-1">meta: máximo 5%</p>
              </CardContent>
            </Card>
          </div>
          </div>

          {/* Tabela de Pedidos */}
          <Card>
            <CardHeader>
              <CardTitle>📦 Todos os Pedidos</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Filtro de Busca */}
              <div className="mb-6 flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    placeholder="Buscar por pedido, cliente, NFe..."
                    value={filtroPedidosTransp}
                    onChange={(e) => {
                      setFiltroPedidosTransp(e.target.value);
                      setPaginaPedidosTransp(1); // Resetar página ao filtrar
                    }}
                    className="pl-10"
                  />
                </div>
                {filtroPedidosTransp && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setFiltroPedidosTransp('');
                      setPaginaPedidosTransp(1);
                    }}
                  >
                    Limpar
                  </Button>
                )}
              </div>

              {(() => {
                // Aplicar filtro de período primeiro
                const pedidosPeriodo = filtrarPedidosPorPeriodo(pedidosTransportadora);
                
                console.log('🗓️ Filtro de período aplicado:', {
                  mes: filtroMes,
                  ano: filtroAno, 
                  total_antes: pedidosTransportadora.length,
                  total_depois: pedidosPeriodo.length
                });
                
                // Depois aplicar filtro de busca
                const pedidosFiltrados = pedidosPeriodo.filter(pedido => {
                  if (!filtroPedidosTransp.trim()) return true;
                  const termo = filtroPedidosTransp.toLowerCase();
                  return (
                    (pedido.pedido && pedido.pedido.toLowerCase().includes(termo)) ||
                    (pedido.nome_cliente && pedido.nome_cliente.toLowerCase().includes(termo)) ||
                    (pedido.cliente && pedido.cliente.toLowerCase().includes(termo)) ||
                    (pedido.nfe && pedido.nfe.toLowerCase().includes(termo))
                  );
                });

                // Calcular paginação
                const totalPaginas = Math.ceil(pedidosFiltrados.length / itensPorPaginaPedidos);
                const inicio = (paginaPedidosTransp - 1) * itensPorPaginaPedidos;
                const fim = inicio + itensPorPaginaPedidos;
                const pedidosPaginados = pedidosFiltrados.slice(inicio, fim);

                return (
                  <>
                    {/* Contador */}
                    <p className="text-sm text-gray-600 mb-4">
                      Mostrando {inicio + 1}-{Math.min(fim, pedidosFiltrados.length)} de {pedidosFiltrados.length} pedidos
                    </p>

                    {/* Tabela */}
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left p-3 text-sm font-semibold">Pedido</th>
                            <th className="text-left p-3 text-sm font-semibold">Cliente</th>
                            <th className="text-left p-3 text-sm font-semibold">Status</th>
                            <th className="text-right p-3 text-sm font-semibold">Valor NF</th>
                            <th className="text-right p-3 text-sm font-semibold">Valor Frete</th>
                            <th className="text-center p-3 text-sm font-semibold">% Frete</th>
                            <th className="text-center p-3 text-sm font-semibold">CTE</th>
                            <th className="text-center p-3 text-sm font-semibold">Tipo Frete</th>
                            <th className="text-left p-3 text-sm font-semibold">Prazo Prometido</th>
                            <th className="text-left p-3 text-sm font-semibold">Entrega Real</th>
                            <th className="text-center p-3 text-sm font-semibold">Performance</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pedidosPaginados.length === 0 ? (
                            <tr>
                              <td colSpan="11" className="text-center py-12 text-gray-500">
                                Nenhum pedido encontrado
                              </td>
                            </tr>
                          ) : (
                            pedidosPaginados.map((pedido, idx) => {
                      const prazoPromise = pedido.prev_entrega ? parseDate(pedido.prev_entrega) : null;
                      const entregaReal = pedido.data_entrega_real ? parseDate(pedido.data_entrega_real) : null;
                      
                      // Cálculo de Performance de Prazo
                      let performanceClass = '';
                      let performanceText = '-';
                      
                      if (prazoPromise && entregaReal && !isNaN(prazoPromise.getTime()) && !isNaN(entregaReal.getTime())) {
                        const diffDias = Math.ceil((entregaReal - prazoPromise) / (1000 * 60 * 60 * 24));
                        if (diffDias <= 0) {
                          performanceClass = 'text-green-600 bg-green-50';
                          performanceText = diffDias === 0 ? 'No prazo' : `${Math.abs(diffDias)}d antes`;
                        } else {
                          performanceClass = 'text-red-600 bg-red-50';
                          performanceText = `${diffDias}d atraso`;
                        }
                      }

                      // Cálculo de % Frete
                      let percentFrete = null;
                      let percentFreteClass = '';
                      let percentFreteText = '-';
                      
                      // Não calcular margem de frete para FOB ou quando frete está embutido na NF
                      const isFOB = pedido.tipo_frete === 'FOB';
                      const isFreteEmbutido = pedido.cte_info && pedido.cte_info.frete_embutido_nf === true;
                      
                      if (isFOB || isFreteEmbutido) {
                        percentFreteText = 'N/A';
                        percentFreteClass = 'text-gray-500 bg-gray-50 italic text-xs';
                      } else if (pedido.valor_frete && pedido.valor_nf) {
                        percentFrete = (parseValorMonetario(pedido.valor_frete) / parseValorMonetario(pedido.valor_nf)) * 100;
                        percentFreteText = `${percentFrete.toFixed(2)}%`;
                        
                        if (percentFrete <= 5) {
                          percentFreteClass = 'text-green-600 bg-green-50 font-semibold';
                        } else {
                          percentFreteClass = 'text-red-600 bg-red-50 font-semibold';
                        }
                      }
                      
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-medium">{pedido.pedido}</td>
                          <td className="p-3 text-sm">{pedido.nome_cliente || pedido.cliente || '-'}</td>
                          <td className="p-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              pedido.status_pedido === 'ENTREGUE' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-orange-100 text-orange-800'
                            }`}>
                              {pedido.status_pedido}
                            </span>
                          </td>
                          <td className="p-3 text-right text-sm">
                            {pedido.valor_nf ? `R$ ${parseValorMonetario(pedido.valor_nf).toFixed(2)}` : '-'}
                          </td>
                          <td className="p-3 text-right font-medium">
                            {pedido.valor_frete ? `R$ ${parseValorMonetario(pedido.valor_frete).toFixed(2)}` : '-'}
                          </td>
                          <td className="p-3 text-center">
                            {percentFreteText === 'N/A' ? (
                              <span className={`px-2 py-1 rounded text-xs ${percentFreteClass}`} title="Não aplicável (FOB ou frete embutido)">
                                {percentFreteText}
                              </span>
                            ) : percentFreteText !== '-' ? (
                              <span className={`px-2 py-1 rounded text-xs ${percentFreteClass}`}>
                                {percentFreteText}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {pedido.numero_cte ? (
                              <span className="text-xs font-mono bg-blue-50 text-blue-800 px-2 py-1 rounded">
                                {pedido.numero_cte}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            {pedido.tipo_frete ? (
                              <span className={`px-2 py-1 rounded text-xs font-semibold ${
                                pedido.tipo_frete === 'CIF' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-blue-100 text-blue-800'
                              }`}>
                                {pedido.tipo_frete}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3 text-sm">
                            {formatDateBR(pedido.prev_entrega)}
                          </td>
                          <td className="p-3 text-sm">
                            {formatDateOnlyBR(pedido.data_entrega_real)}
                          </td>
                          <td className="p-3 text-center">
                            {performanceText !== '-' ? (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${performanceClass}`}>
                                {performanceText}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                        </tr>
                            );
                          })
                        )}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginação */}
                    {totalPaginas > 1 && (
                      <div className="mt-6 flex items-center justify-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaginaPedidosTransp(prev => Math.max(1, prev - 1))}
                          disabled={paginaPedidosTransp === 1}
                        >
                          <ChevronLeft size={16} />
                        </Button>

                        <div className="flex gap-1">
                          {Array.from({ length: Math.min(totalPaginas, 10) }, (_, i) => {
                            // Mostrar no máximo 10 páginas
                            let pageNum;
                            if (totalPaginas <= 10) {
                              pageNum = i + 1;
                            } else {
                              // Lógica para páginas dinâmicas quando há muitas
                              if (paginaPedidosTransp <= 5) {
                                pageNum = i + 1;
                              } else if (paginaPedidosTransp >= totalPaginas - 4) {
                                pageNum = totalPaginas - 9 + i;
                              } else {
                                pageNum = paginaPedidosTransp - 4 + i;
                              }
                            }
                            
                            return (
                              <Button
                                key={pageNum}
                                variant={paginaPedidosTransp === pageNum ? "default" : "outline"}
                                size="sm"
                                onClick={() => setPaginaPedidosTransp(pageNum)}
                                style={paginaPedidosTransp === pageNum ? { 
                                  backgroundColor: colors.primaza.primary,
                                  color: 'white'
                                } : {}}
                              >
                                {pageNum}
                              </Button>
                            );
                          })}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPaginaPedidosTransp(prev => Math.min(totalPaginas, prev + 1))}
                          disabled={paginaPedidosTransp === totalPaginas}
                        >
                          <ChevronRight size={16} />
                        </Button>
                      </div>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const { resumo, por_status, por_vendedor, por_transportadora } = analytics;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
              📊 Analytics & Performance
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Métricas e indicadores de performance do sistema
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAnalytics}
            disabled={loading}
            style={{ 
              borderColor: colors.primaza.primary,
              color: colors.primaza.secondary 
            }}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      <div className="max-w-full mx-auto p-6 space-y-6">
        {/* KPIs Principais */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card style={{ borderLeft: `4px solid ${colors.primaza.secondary}` }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Package size={24} style={{ color: colors.primaza.secondary }} />
                <TrendingUp size={20} className="text-gray-400" />
              </div>
              <p className="text-sm text-gray-600">Total de Pedidos</p>
              <p className="text-3xl font-bold" style={{ color: colors.primaza.secondary }}>
                {resumo.total_pedidos}
              </p>
            </CardContent>
          </Card>

          <Card style={{ borderLeft: `4px solid ${colors.primaza.primary}` }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <div className="text-2xl">✅</div>
                <TrendingUp size={20} className="text-green-500" />
              </div>
              <p className="text-sm text-gray-600">Pedidos Entregues</p>
              <p className="text-3xl font-bold" style={{ color: colors.primaza.primary }}>
                {resumo.pedidos_entregues}
              </p>
            </CardContent>
          </Card>

          <Card style={{ borderLeft: '4px solid #3B82F6' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <Truck size={24} className="text-blue-600" />
                <TrendingUp size={20} className="text-blue-400" />
              </div>
              <p className="text-sm text-gray-600">Em Trânsito</p>
              <p className="text-3xl font-bold text-blue-600">
                {resumo.em_transito}
              </p>
            </CardContent>
          </Card>

          <Card style={{ borderLeft: '4px solid #EF4444' }}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-2">
                <AlertTriangle size={24} className="text-red-600" />
                <TrendingUp size={20} className="text-red-400" />
              </div>
              <p className="text-sm text-gray-600">Atrasados</p>
              <p className="text-3xl font-bold text-red-600">
                {resumo.atrasados}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Taxa de Entrega */}
        <Card>
          <CardHeader>
            <CardTitle style={{ color: colors.primaza.secondary }}>
              🎯 Taxa de Entrega
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="w-full bg-gray-200 rounded-full h-8">
                  <div 
                    className="h-8 rounded-full flex items-center justify-center text-white font-bold"
                    style={{
                      width: `${resumo.taxa_entrega}%`,
                      backgroundColor: colors.primaza.primary
                    }}
                  >
                    {resumo.taxa_entrega}%
                  </div>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold" style={{ color: colors.primaza.secondary }}>
                  {resumo.taxa_entrega}%
                </p>
                <p className="text-xs text-gray-500">de entregas concluídas</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Card de Performance Geral de Entrega */}
        <Card className="bg-gradient-to-r from-blue-50 to-green-50">
          <CardHeader>
            <CardTitle style={{ color: colors.primaza.secondary }}>
              🎯 Performance Geral de Entrega
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">Indicadores principais de eficiência e custos</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Taxa de Entrega */}
              <div className="text-center p-4 bg-white rounded-lg border-2 border-green-200">
                <div className="text-4xl mb-2">✅</div>
                <p className="text-sm text-gray-600 mb-2">Taxa de Entrega</p>
                <p className="text-4xl font-bold text-green-600">
                  {resumo.total_pedidos > 0 && resumo.pedidos_entregues !== undefined
                    ? ((resumo.pedidos_entregues / resumo.total_pedidos) * 100).toFixed(1) 
                    : 0}%
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  {resumo.pedidos_entregues || 0} de {resumo.total_pedidos} pedidos
                </p>
              </div>

              {/* Entregas no Prazo */}
              <div className="text-center p-4 bg-white rounded-lg border-2 border-blue-200">
                <div className="text-4xl mb-2">⚡</div>
                <p className="text-sm text-gray-600 mb-2">No Prazo ou Antes</p>
                <p className="text-4xl font-bold text-blue-600">-</p>
                <p className="text-xs text-gray-500 mt-2">
                  Dados após preenchimento de prazos
                </p>
              </div>

              {/* Tempo Médio */}
              <div className="text-center p-4 bg-white rounded-lg border-2 border-orange-200">
                <div className="text-4xl mb-2">📦</div>
                <p className="text-sm text-gray-600 mb-2">Transportadoras Ativas</p>
                <p className="text-4xl font-bold text-orange-600">
                  {por_transportadora.length}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Clique abaixo para detalhes
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pedidos por Transportadora */}
        <Card>
          <CardHeader>
            <CardTitle style={{ color: colors.primaza.secondary }}>
              🚚 Performance por Transportadora
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">Clique em uma transportadora para ver detalhes completos</p>
          </CardHeader>
          <CardContent>
            {/* Filtro de Busca */}
            <div className="mb-6 flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  placeholder="Buscar transportadora..."
                  value={filtroTransportadora}
                  onChange={(e) => {
                    setFiltroTransportadora(e.target.value);
                    setPaginaTransportadora(1); // Resetar página ao filtrar
                  }}
                  className="pl-10"
                />
              </div>
              {filtroTransportadora && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setFiltroTransportadora('');
                    setPaginaTransportadora(1);
                  }}
                >
                  Limpar
                </Button>
              )}
            </div>

            {(() => {
              // Aplicar filtro
              const transportadorasFiltradas = por_transportadora.filter(item => {
                if (!filtroTransportadora.trim()) return true;
                const nome = item._id || 'Sem Transportadora';
                return nome.toLowerCase().includes(filtroTransportadora.toLowerCase());
              });

              // Calcular paginação
              const totalPaginas = Math.ceil(transportadorasFiltradas.length / itensPorPaginaTransp);
              const inicio = (paginaTransportadora - 1) * itensPorPaginaTransp;
              const fim = inicio + itensPorPaginaTransp;
              const transportadorasPaginadas = transportadorasFiltradas.slice(inicio, fim);

              return (
                <>
                  {/* Grid de Transportadoras */}
                  {transportadorasPaginadas.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Truck size={48} className="mx-auto mb-3 text-gray-300" />
                      <p>Nenhuma transportadora encontrada</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* Card Especial: TODOS OS PEDIDOS */}
                      <div 
                        className="p-6 border-4 rounded-xl hover:shadow-xl transition-all cursor-pointer col-span-full md:col-span-2 lg:col-span-3"
                        style={{ 
                          borderColor: colors.primaza.primary,
                          background: `linear-gradient(135deg, ${colors.primaza.primary}15 0%, ${colors.primaza.secondary}15 100%)`
                        }}
                        onClick={async () => {
                          // Carregar TODOS os pedidos
                          setLoading(true);
                          try {
                            const token = localStorage.getItem('primaza_token');
                            const response = await axios.get(`${API}/pedidos?limit=5000`, {
                              headers: { Authorization: `Bearer ${token}` }
                            });
                            
                            const todosPedidos = response.data.pedidos || [];
                            setPedidosTransportadora(todosPedidos);
                            setSelectedTransportadora({
                              nome: 'TODAS AS TRANSPORTADORAS',
                              pedidos: todosPedidos,
                              total: todosPedidos.length,
                              entregues: todosPedidos.filter(p => p.status_pedido === 'ENTREGUE').length,
                              valorTotal: todosPedidos.reduce((sum, p) => sum + (parseValorMonetario(p.valor_frete) || 0), 0),
                              pedidosComDatas: todosPedidos.filter(p => p.prev_entrega && p.data_entrega_real).length
                            });
                          } catch (error) {
                            console.error('Erro:', error);
                          } finally {
                            setLoading(false);
                          }
                        }}
                      >
                        <div className="flex items-center gap-4">
                          <div className="p-4 rounded-xl" style={{ backgroundColor: colors.primaza.primary }}>
                            <Package size={32} className="text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold mb-1" style={{ color: colors.primaza.secondary }}>
                              📦 TODAS AS TRANSPORTADORAS
                            </h3>
                            <p className="text-sm text-gray-600 mb-2">
                              Visão geral completa de todos os {resumo.total_pedidos} pedidos
                            </p>
                            <div className="flex gap-4 text-sm">
                              <span className="text-green-600 font-semibold">
                                ✅ {resumo.pedidos_entregues} Entregues
                              </span>
                              <span className="text-blue-600 font-semibold">
                                🚚 {resumo.em_transito} Em Trânsito
                              </span>
                              <span className="text-red-600 font-semibold">
                                ⚠️ {resumo.atrasados} Em Atraso
                              </span>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-gray-500 mb-1">Clique para ver detalhes</p>
                            <Button
                              size="sm"
                              style={{ backgroundColor: colors.primaza.primary }}
                              className="text-white"
                            >
                              Ver Todos
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Cards das Transportadoras */}
                      {transportadorasPaginadas.map((item, idx) => (
                        <div 
                          key={idx} 
                          className="p-4 border rounded-lg hover:shadow-lg transition-all cursor-pointer hover:border-blue-500 relative"
                          onClick={() => loadPedidosTransportadora(item._id)}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-2xl">🚚</div>
                            <p className="text-2xl font-bold" style={{ color: colors.primaza.primary }}>
                              {item.count}
                            </p>
                          </div>
                          <p className="font-medium text-gray-900">{item._id || 'Sem Transportadora'}</p>
                          <p className="text-xs text-gray-500 mt-1">{item.count} pedidos processados</p>
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-blue-600 font-medium">
                              → Clique para ver detalhes
                            </p>
                          </div>
                        </div>
                      ))}
                      
                      {/* Overlay de Loading ao carregar transportadora */}
                      {loadingTransportadora && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                          <div className="bg-white rounded-xl p-8 shadow-2xl flex flex-col items-center gap-4 min-w-[320px]">
                            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
                            <p className="text-lg font-medium text-gray-700">Carregando pedidos...</p>
                            
                            {/* Barra de Progresso */}
                            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                              <div 
                                className="bg-blue-600 h-3 rounded-full transition-all duration-300 ease-out"
                                style={{ width: `${Math.min(Math.round(loadingProgress), 100)}%` }}
                              />
                            </div>
                            <p className="text-sm text-gray-500">{Math.min(Math.round(loadingProgress), 100)}% concluído</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Paginação e Contador */}
                  {transportadorasFiltradas.length > 0 && (
                    <div className="mt-6 flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Mostrando {inicio + 1}-{Math.min(fim, transportadorasFiltradas.length)} de {transportadorasFiltradas.length} transportadoras
                      </p>

                      {totalPaginas > 1 && (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaginaTransportadora(prev => Math.max(1, prev - 1))}
                            disabled={paginaTransportadora === 1}
                          >
                            <ChevronLeft size={16} />
                          </Button>

                          <div className="flex gap-1">
                            {Array.from({ length: totalPaginas }, (_, i) => i + 1).map(num => (
                              <Button
                                key={num}
                                variant={paginaTransportadora === num ? "default" : "outline"}
                                size="sm"
                                onClick={() => setPaginaTransportadora(num)}
                                style={paginaTransportadora === num ? { 
                                  backgroundColor: colors.primaza.primary,
                                  color: 'white'
                                } : {}}
                              >
                                {num}
                              </Button>
                            ))}
                          </div>

                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaginaTransportadora(prev => Math.min(totalPaginas, prev + 1))}
                            disabled={paginaTransportadora === totalPaginas}
                          >
                            <ChevronRight size={16} />
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Analytics;
